/**
 * Cache wrapper for the unscheduled-jobs backlog (`/get-unscheduled`). The endpoint POSTs an empty
 * body and is scoped entirely server-side by the auth/impersonation context — it is NOT scoped to
 * the dashboard's `?company=` selector (the backlog spans every company the scheduler can see). So
 * the cache keys by the impersonation context: a developer impersonating a user gets that user's
 * backlog under a distinct key, while their own backlog stays under "self". The admin "Show Test
 * Jobs" setting widens what the backend returns too, so it is part of the key as well.
 *
 * TtlCache (15-min, in-flight coalescing) means the two enrichment passes / a StrictMode double-mount
 * collapse to one fetch, and an in-app remount is instant. Mutations that change the backlog (saving
 * shift assignments) must call `clear()` — `Scheduling.vm` does so in `savePendingShifts`.
 */

import { Job } from "@domain/CRM/job";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";
import { getImpersonatedEmail } from "../../../storage/impersonationStore";
import { showTestJobsStore } from "../../../storage/showTestJobsStore";

/** The lifetime of both job-list caches; the Scheduling board's incoming-jobs look reads it. */
export const BACKLOG_TTL_MS = 15 * 60 * 1000;
const SCHEDULED_TTL_MS = BACKLOG_TTL_MS;
const SELF_KEY = "self" as const;

class CachedUnscheduledJobs {
	/**
	 * Per-key batch listeners for an in-flight streaming fetch. TtlCache coalesces concurrent
	 * `get()` calls for the same key into one fetch, so each caller registers its own `onBatch`
	 * here and every batch fans out to all current listeners. A cache hit fires none.
	 */
	private batchListeners = new Map<string, Set<(jobs: Job[]) => void>>();

	private cache = new TtlCache<string, Job[]>(BACKLOG_TTL_MS, (key) =>
		API.Data.Jobs.get.unscheduledStream((batch) => {
			const listeners = this.batchListeners.get(key);
			if (listeners) for (const listener of listeners) listener(batch);
		}),
	);

	/**
	 * Cache key = the two axes the backend scopes the backlog by: the active impersonation context,
	 * and whether "Show Test Jobs" is on. Toggling that setting reloads the page today, which empties
	 * this in-memory cache anyway — keying on it means the backlog stays right without the reload.
	 */
	private key(): string {
		const context = getImpersonatedEmail() ?? SELF_KEY;
		return showTestJobsStore.get() ? `${context}:test-jobs` : context;
	}

	/** Synchronous TTL cache hit for the current context, or null if missing/expired. */
	public peek(): Job[] | null {
		return this.cache.peek(this.key());
	}

	/**
	 * Resolves with the full backlog. On a cache miss the fetch streams; `onBatch`
	 * fires with each batch so the caller can render progressively. On a cache hit
	 * (or when coalescing into an already-in-flight fetch started by another
	 * caller) `onBatch` may never fire — always use the resolved array as the
	 * authoritative result. Concurrent callers each receive every batch.
	 */
	public get(onBatch?: (jobs: Job[]) => void): Promise<Job[]> {
		const key = this.key();
		if (!onBatch) return this.cache.get(key);

		let listeners = this.batchListeners.get(key);
		if (!listeners) {
			listeners = new Set();
			this.batchListeners.set(key, listeners);
		}
		listeners.add(onBatch);

		const detach = () => {
			const current = this.batchListeners.get(key);
			if (!current) return;
			current.delete(onBatch);
			if (current.size === 0) this.batchListeners.delete(key);
		};
		return this.cache.get(key).finally(detach);
	}

	/** Rewrites the current context's backlog in place (keeping its age), if one is cached — for a
	 *  save that re-read the few jobs it changed. */
	public update(change: (jobs: Job[]) => Job[]): void {
		this.cache.update(this.key(), change);
	}

	/** Drops every context's entry — call after a mutation that changes the backlog. */
	public clear(): void {
		this.cache.clear();
		this.batchListeners.clear();
	}
}

/**
 * The scheduling board's scheduled jobs (`/get-scheduled-unsampled`): every job on a shift that
 * is not sampled yet. Cached exactly like the backlog above — 15 minutes, in-flight coalescing —
 * so leaving the board and coming back shows both lists at once; the board treats the two alike.
 *
 * A save and the board's Refresh call `refresh()`: always a new request (one already under way may
 * predate the write), whose result replaces the entry — unless a later `refresh()` has landed
 * first. A failed refresh leaves the entry as it was.
 *
 * Scoped server-side by the auth/impersonation context like the backlog, hence the key. "Show Test
 * Jobs" is not part of it: this read shows staff their test jobs whatever that setting says.
 */
class CachedScheduledUnsampledJobs {
	private cache = new TtlCache<string, Job[]>(SCHEDULED_TTL_MS, () => API.Data.Jobs.get.scheduledUnsampled());
	private latestRefresh = 0;

	/** Synchronous TTL cache hit for the current context, or null if missing/expired. */
	public peek(): Job[] | null {
		return this.cache.peek(this.key());
	}

	/** Cache hit, a read already in flight, or a new read (in that priority) — for a mount. */
	public get(): Promise<Job[]> {
		return this.cache.get(this.key());
	}

	/** Rewrites the current context's list in place (keeping its age), if one is cached — for a
	 *  save that re-read the few jobs it changed. */
	public update(change: (jobs: Job[]) => Job[]): void {
		this.cache.update(this.key(), change);
	}

	/** Always a new request, for a caller whose own write (a save) or explicit gesture (Refresh) a
	 *  read already under way may predate. */
	public async refresh(): Promise<Job[]> {
		const key = this.key();
		const token = ++this.latestRefresh;
		const jobs = await API.Data.Jobs.get.scheduledUnsampled();
		if (token === this.latestRefresh) this.cache.set(key, jobs);
		return jobs;
	}

	private key(): string {
		return getImpersonatedEmail() ?? SELF_KEY;
	}
}

export class CachedJobsAPI {
	public unscheduled = new CachedUnscheduledJobs();
	public scheduledUnsampled = new CachedScheduledUnsampledJobs();
}
